import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { copyToClipboard } from "@redotech/redo-web/clipboard";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import ShopifyBagLogo from "@redotech/redo-web/shopify/bag-logo.svg";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo } from "react";
import type { OrderWithPickStatus } from "../db/collections";
import { useCustomerOrders } from "../hooks/use-customer-orders";
import { CollapsibleSection } from "./collapsible-section";
import { CustomerSelector } from "./customer-selector";
import { CustomerTotals } from "./customer-totals";
import { FieldLabel } from "./field-label";

export const CustomerInfoSection = memo(function CustomerInfoSection({
  conversation,
}: {
  conversation: SupportTicketListItemResponse;
}) {
  const team = useRequiredContext(TeamContext);
  const customer = conversation.customer;
  const customerEmail = customer?.email;
  const { orders, pending: ordersPending } = useCustomerOrders(customerEmail);

  const handleCopyName = useHandler(() => {
    void copyToClipboard(customer?.displayName || "Unknown");
  });

  const shopifyUrl = getShopifyCustomerLink(team.storeUrl, orders);

  const handleViewOnShopify = useHandler(() => {
    if (shopifyUrl) {
      window.open(shopifyUrl, "_blank");
    }
  });

  return (
    <CollapsibleSection
      localStorageKey="redo.support-v2.conversation-details.customer.collapsed"
      title="Customer"
    >
      <Flex align="center" gap="md" justify="space-between">
        <FieldLabel>Name</FieldLabel>
        <Flex align="center" gap="xxs">
          <Flex align="center" gap="none">
            <RedoButton
              hierarchy="tertiary"
              IconLeading={CopyIcon}
              onClick={handleCopyName}
              size="xs"
            />
            {customer?.id && (
              <RedoButton
                hierarchy="tertiary"
                IconLeading={LinkExternalIcon}
                onClick={() =>
                  window.open(
                    `/stores/${team._id}/customer/${customer.id}`,
                    "_blank",
                  )
                }
                size="xs"
              />
            )}
          </Flex>
          <CustomerSelector
            customer={customer}
            supportTicketId={conversation.id}
          />
        </Flex>
      </Flex>

      {customerEmail && (
        <Flex align="center" gap="md" justify="space-between">
          <FieldLabel>Email</FieldLabel>
          <CopyableText
            fontSize="xs"
            textColor="primary"
            textOverflow="ellipsis"
          >
            {customerEmail}
          </CopyableText>
        </Flex>
      )}

      <CustomerTagsPlaceholder />

      <CustomerTotals orders={orders} pending={ordersPending} />

      {shopifyUrl && (
        <RedoButton
          hierarchy="secondary"
          IconLeading={ShopifyBagLogo}
          onClick={handleViewOnShopify}
          size="xs"
          text="View on Shopify"
        />
      )}
    </CollapsibleSection>
  );
});

function getShopifyCustomerLink(
  storeUrl: string | undefined,
  orders: OrderWithPickStatus[],
): string | undefined {
  const customerId = orders
    .map((order) => order.shopify.customer?.id)
    .find((id) => id != null);
  if (!customerId) {
    return undefined;
  }
  const regexMatch = storeUrl?.match(/(.*)\.myshopify.com/);
  const storeName = regexMatch ? regexMatch[1] : undefined;
  if (!storeName) {
    return undefined;
  }
  return `https://admin.shopify.com/store/${storeName}/customers/${customerId}`;
}

const CustomerTagsPlaceholder = memo(function CustomerTagsPlaceholder() {
  return (
    <Flex align="center" gap="md" justify="space-between">
      <FieldLabel>Customer tags</FieldLabel>
      <Tooltip title="No customer tags available">
        <Flex style={{ opacity: 0.3 }}>
          <RedoBadge color="gray" shadow size="sm" text="+" />
        </Flex>
      </Tooltip>
    </Flex>
  );
});
